import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { getCreditTransactions } from '@/lib/api'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Loader2 } from 'lucide-react'
import { formatDate } from '@/lib/utils'

interface CreditTransactionListProps {
  serviceCode: string
  externalAccountId: string
}

const TYPE_COLORS: Record<string, string> = {
  purchase: 'bg-green-100 text-green-800',
  usage: 'bg-orange-100 text-orange-800',
  bonus: 'bg-blue-100 text-blue-800',
  refund: 'bg-purple-100 text-purple-800',
  correction: 'bg-gray-100 text-gray-800',
  expiry: 'bg-red-100 text-red-800',
}

export function CreditTransactionList({ serviceCode, externalAccountId }: CreditTransactionListProps) {
  const [limit, setLimit] = useState(10)

  const { data: transactions, isLoading, error } = useQuery({
    queryKey: ['credit-transactions', serviceCode, externalAccountId, limit],
    queryFn: () => getCreditTransactions(serviceCode, externalAccountId, limit, 0),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading transactions...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-4 text-sm text-destructive">
        Error loading transactions: {error.message}
      </div>
    )
  }

  if (!transactions || transactions.length === 0) {
    return (
      <div className="py-4 text-sm text-muted-foreground">
        No transactions yet
      </div>
    )
  }

  const hasMore = transactions.length === limit
  const showViewAll = limit >= 30 && hasMore

  return (
    <div className="py-2">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-[140px]">Date</TableHead>
            <TableHead className="w-[80px] text-right">Amount</TableHead>
            <TableHead className="w-[100px]">Type</TableHead>
            <TableHead>Resource</TableHead>
            <TableHead>Description</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {transactions.map((tx) => (
            <TableRow key={tx.id} className="hover:bg-transparent">
              <TableCell className="text-muted-foreground text-sm">
                {formatDate(tx.created_at)}
              </TableCell>
              <TableCell className={`text-right font-medium ${tx.amount > 0 ? 'text-green-600' : 'text-orange-600'}`}>
                {tx.amount > 0 ? '+' : ''}{tx.amount}
              </TableCell>
              <TableCell>
                <Badge variant="secondary" className={TYPE_COLORS[tx.transaction_type] || ''}>
                  {tx.transaction_type}
                </Badge>
              </TableCell>
              <TableCell className="font-medium">
                {tx.resource_name || '-'}
              </TableCell>
              <TableCell className="text-muted-foreground text-sm truncate max-w-[200px]" title={tx.description || ''}>
                {tx.description || '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <div className="flex justify-center gap-2 pt-2">
        {hasMore && !showViewAll && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLimit((prev) => prev + 10)}
          >
            Load more...
          </Button>
        )}
        {showViewAll && (
          <Button variant="link" size="sm" asChild>
            <a href={`/credits/transactions?service=${serviceCode}&account=${externalAccountId}`}>
              View all transactions →
            </a>
          </Button>
        )}
      </div>
    </div>
  )
}
